// TableComponent.jsx
import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import "./TableComponent.css";
import { useNavigate } from "react-router-dom";
import TableHeader from "./TableHeader";
import TableRow from "./TableRow";
import TablePagination from "./TablePagination";
import TableBulkActionBar from "./TableBulkActionBar";
import TableColumnFilters from "./TableColumnFilters";
import TableExcelActions from "./TableExcelActions";
import TableLoader from "./TableLoader";
import { getValue } from "./utils";
import TableRowDetailsModal from "./TableRowDetailsModal";
import DynamicButton from "../DynamicButton/DynamicButton";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";

/* ---- helpers for date-range filtering ---- */
const toYYYYMMDD = (v) => {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const yr = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const da = String(d.getUTCDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
};

function parseRowDate(rowVal) {
  if (!rowVal) return NaN;
  // Handles ISO strings like "2025-08-13T00:00:00.000Z" or "YYYY-MM-DD"
  const d = new Date(rowVal);
  return d.getTime();
}

function dateInRange(rowVal, fromYmd, toYmd) {
  const t = parseRowDate(rowVal);
  if (!Number.isFinite(t)) return false; // invalid row date => fail match when range provided
  let ok = true;
  if (fromYmd) {
    const fromT = new Date(`${fromYmd}T00:00:00Z`).getTime();
    if (Number.isFinite(fromT)) ok = ok && t >= fromT;
  }
  if (toYmd) {
    const toT = new Date(`${toYmd}T23:59:59Z`).getTime();
    if (Number.isFinite(toT)) ok = ok && t <= toT;
  }
  return ok;
}



const TableComponent = ({ data = [], columns = [], api, model ,title}) => {
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // --- inside component state ---
  const [itemsPerPage, setItemsPerPage] = useState(10); // default 10

  // modal state
  const [showDetails, setShowDetails] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const loop = false;


  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(api);
        setTableData(Array.isArray(res.data) ? res.data : res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const handleEdit = (id) => {
    if (!id) return;
    navigate(`/edit/${model}/${id}`);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${api}/${id}`);
      setTableData((prev) => prev.filter((item) => item._id !== id));
      // also clean selection if present
      setSelectedRows((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

const handleBulkDelete = async () => {
  try {
    await Promise.all(selectedRows.map((id) => axios.delete(`${api}/${id}`)));
    setTableData((prev) =>
      prev.filter((row) => !selectedRows.includes(row._id))
    );
    setSelectedRows([]);
  } catch (err) {
    console.error("Bulk delete failed", err);
  } finally {
    setShowBulkConfirm(false); // close popup after action
  }
};




  const toggleRowSelect = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

const openDetails = (row) => {
  // Prefer matching by _id
  const idx = sortedData.findIndex((r) => r?._id && r._id === row?._id) ?? -1;

  // Fallback: identity match if _id missing
  const finalIdx = idx >= 0 ? idx : sortedData.indexOf(row);

  if (finalIdx < 0) return; // row not found in current view
  setActiveIndex(finalIdx);
  setActiveRow(sortedData[finalIdx]);
  setShowDetails(true);
};

const closeDetails = () => {
  setActiveRow(null);
  setActiveIndex(null);
  setShowDetails(false);
};

  const handleEditFromModal = () => {
    if (activeRow?._id) handleEdit(activeRow._id);
  };
const handleDeleteFromModal = async () => {
  if (activeRow?._id) {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this record? This action cannot be undone."
    );

    if (!confirmDelete) return; // exit if user cancels

    await handleDelete(activeRow._id);
    closeDetails();
  }
};


  // build a quick lookup of date-range filters: { field: {from,to} }
  const dateRangeFilters = useMemo(() => {
    const map = {};
    for (const [k, v] of Object.entries(columnFilters || {})) {
      const m = k.match(/^(.*)__(from|to)$/i);
      if (m) {
        const base = m[1];
        const edge = m[2].toLowerCase();
        map[base] = map[base] || { from: "", to: "" };
        map[base][edge] = toYYYYMMDD(v);
      }
    }
    return map;
  }, [columnFilters]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return (tableData || []).filter((row) => {
      // global search over visible columns
      const searchMatch = term
        ? columns.some((col) =>
            String(getValue(row, col.field) ?? "")
              .toLowerCase()
              .includes(term)
          )
        : true;

      if (!searchMatch) return false;

      // column filters
      // 1) handle date ranges first
      for (const [field, range] of Object.entries(dateRangeFilters)) {
        const rowVal = getValue(row, field);
        const pass = dateInRange(rowVal, range.from, range.to);
        if (!pass) return false;
      }

      // 2) handle simple includes filters (keys that are NOT __from/__to and not part of a date range)
      for (const [field, val] of Object.entries(columnFilters || {})) {
        if (/__from$|__to$/i.test(field)) continue; // already handled via dateRangeFilters
        const rowVal = String(getValue(row, field) ?? "").toLowerCase();
        const needle = String(val ?? "").toLowerCase();
        if (needle && !rowVal.includes(needle)) return false;
      }

      return true;
    });
  }, [tableData, columns, searchTerm, columnFilters, dateRangeFilters]);

  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });

  const handleSort = (field) => {
    const direction =
      sortConfig.field === field && sortConfig.direction === "asc"
        ? "desc"
        : "asc";
    setSortConfig({ field, direction });
  };

  const sortedData = useMemo(() => {
    const arr = [...filteredData];
    if (!sortConfig.field) return arr;
    return arr.sort((a, b) => {
      const aVal = getValue(a, sortConfig.field);
      const bVal = getValue(b, sortConfig.field);
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

const totalPages = Math.ceil(sortedData.length / itemsPerPage);

const paginatedData = useMemo(
  () =>
    sortedData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    ),
  [sortedData, currentPage, itemsPerPage]
);


  // --- Select All Checkbox Logic ---
  const isAllSelected =
    paginatedData.length > 0 && selectedRows.length === paginatedData.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // select all row IDs from current page
      setSelectedRows(paginatedData.map((row) => row._id));
    } else {
      // clear selection
      setSelectedRows([]);
    }
  };

  useEffect(() => {
    if (!showDetails) return;
    if (!Array.isArray(sortedData) || sortedData.length === 0) {
      setShowDetails(false);
      setActiveRow(null);
      setActiveIndex(null);
      return;
    }

    // clamp index to bounds
    const clamped =
      activeIndex == null
        ? 0
        : Math.min(Math.max(activeIndex, 0), sortedData.length - 1);

    setActiveIndex(clamped);
    setActiveRow(sortedData[clamped]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedData, showDetails]);

  const hasPrev = activeIndex != null && activeIndex > 0;
  const hasNext = activeIndex != null && activeIndex < sortedData.length - 1;

  const handlePrev = () => {
    if (!showDetails || activeIndex == null) return;
    if (!sortedData.length) return;

    let nextIdx = activeIndex - 1;
    if (nextIdx < 0) {
      if (!loop) return;
      nextIdx = (activeIndex - 1 + sortedData.length) % sortedData.length;
    }
    setActiveIndex(nextIdx);
    setActiveRow(sortedData[nextIdx]);
  };

  const handleNext = () => {
    if (!showDetails || activeIndex == null) return;
    if (!sortedData.length) return;

    let nextIdx = activeIndex + 1;
    if (nextIdx >= sortedData.length) {
      if (!loop) return;
      nextIdx = (activeIndex + 1) % sortedData.length;
    }
    setActiveIndex(nextIdx);
    setActiveRow(sortedData[nextIdx]);
  };



  if (loading) return <TableLoader />;

  return (
    <div className="table-container">
      <div className="table-header">
        <h2 className="MainTitleTable">{title}</h2>

        <DynamicButton
          text="Add New Entry"
          icon="fa-plus"
          size="md"
          color="#5046E5"
          hoverColor="#3f34daff"
          link={`/form/${model}`}
        />
      </div>

      <div className="table-header-controls">
        <div className="controls-left">
          <TableExcelActions
            columns={columns}
            filteredData={filteredData}
            api={api}
          />
          <TableColumnFilters
            columns={columns}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            setCurrentPage={setCurrentPage}
          />
          <TableBulkActionBar
            selectedRows={selectedRows}
            handleBulkDelete={() => setShowBulkConfirm(true)}
          />
        </div>

        <div className="controls-right">
          <input
            type="text"
            placeholder="Search..."
            className="searchbar-input"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>
      <div className="table-responsive">
        <table className="modern-table">
          <TableHeader
            columns={columns}
            sortConfig={sortConfig}
            handleSort={handleSort}
            handleSelectAll={handleSelectAll} // <-- new prop
            isAllSelected={isAllSelected}
          />
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 2} className="no-data">
                  No data found
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <TableRow
                  key={row._id || rowIndex}
                  row={row}
                  index={rowIndex}
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  columns={columns}
                  handleEdit={() => handleEdit(row._id)}
                  handleDelete={handleDelete}
                  selectedRows={selectedRows}
                  toggleRowSelect={toggleRowSelect}
                  onRowClick={openDetails} // <-- open modal on click
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        totalEntries={filteredData.length}
      />

      {/* Details modal */}
      <TableRowDetailsModal
        open={showDetails}
        row={activeRow}
        columns={columns}
        onClose={closeDetails}
        onEdit={handleEditFromModal}
        onDelete={handleDeleteFromModal}
        title={`${model?.toUpperCase?.() || "Record"} Details`}
        onPrev={handlePrev}
        onNext={handleNext}
        hasPrev={hasPrev}
        hasNext={hasNext}
        loop={loop}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        open={showBulkConfirm}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedRows.length} record(s)? This action cannot be undone.`}
        onCancel={() => setShowBulkConfirm(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  );
};

export default TableComponent;
